import React, { useEffect, useState } from "react";
import * as dmsService from "../../../services/dmsService";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import { ActionHistory, DmsBadge, DocHead, Panel, Section, run } from "../sales/dmsUi";
import { AppDropdown, DetailGrid, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, formatDate, inputStyle, pageStyle, useLoad } from "../../../components/ui";

// F&I Deals: how a sales order is paid for (cash, finance, lease) and the products sold with it.
// Lender's answer, contract signature, funding (products invoiced, the amount financed moved to the
// lender with the dealer reserve), then the deal closes when the lender pays.

const TYPES = [
  { label: "Cash", value: "CASH" },
  { label: "Finance", value: "FINANCE" },
  { label: "Lease", value: "LEASE" },
];
const label = (s) => String(s || "").charAt(0) + String(s || "").slice(1).toLowerCase().replace(/_/g, " ");

function DealEditorPanel({ deal, onHide, onDone }) {
  const orders = useLoad(() => (deal ? Promise.resolve({ data: [] }) : dmsService.listOrders({ open: "1", size: 200 })), [deal]);
  const opts = useDmsOptions(["fi-lenders", "fi-products"]);
  const [h, setH] = useState({ so_id: null, deal_type: "FINANCE", lender_id: null, down_payment: "", term_months: 48, apr_pct: "", residual_value: "" });
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (deal) {
      setH({ deal_type: deal.deal_type, lender_id: (opts["fi-lenders"] || []).find((o) => o.code === deal.lender_code)?.value ?? null, down_payment: Number(deal.down_payment), term_months: deal.term_months || "", apr_pct: deal.apr_pct === null ? "" : Number(deal.apr_pct), residual_value: Number(deal.residual_value) });
      setProducts(deal.products.map((p) => ({ product_id: (opts["fi-products"] || []).find((o) => o.code === p.code)?.value ?? null, price: Number(p.price) })));
    }
  }, [deal, opts]);
  const save = async () => {
    setBusy(true);
    const body = {
      ...(deal ? {} : { so_id: h.so_id }),
      deal_type: h.deal_type,
      lender_id: h.deal_type === "CASH" ? undefined : h.lender_id,
      down_payment: Number(h.down_payment || 0),
      term_months: h.deal_type === "CASH" ? undefined : Number(h.term_months),
      apr_pct: h.deal_type === "CASH" ? undefined : Number(h.apr_pct || 0),
      residual_value: h.deal_type === "LEASE" ? Number(h.residual_value || 0) : undefined,
      products: products.filter((p) => p.product_id).map((p) => ({ product_id: p.product_id, price: p.price === "" ? undefined : Number(p.price) })),
    };
    const res = await run(() => dmsService.saveFiDeal(deal ? deal.id : null, body));
    setBusy(false);
    if (res) {
      onDone(deal ? deal.id : res.data.id);
      onHide();
    }
  };
  const set = (i, patch) => setProducts(products.map((p, k) => (k === i ? { ...p, ...patch } : p)));
  return (
    <Panel title={deal ? `Deal ${deal.deal_number}` : "New F&I deal"} onHide={() => !busy && onHide()} width={860} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Save and calculate</PrimaryButton></>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
        {!deal ? (
          <Field label="Sales order" required>
            <AppDropdown id="fi-so" value={h.so_id} filter options={(orders.data || []).filter((o) => o.status !== "ENTERED").map((o) => ({ label: `${o.so_number} – ${o.customer_name} (${fmtMoney(o.total)})`, value: o.id }))} onChange={(e) => setH({ ...h, so_id: e.value })} />
          </Field>
        ) : null}
        <Field label="How the customer pays" required>
          <AppDropdown id="fi-type" value={h.deal_type} options={TYPES} onChange={(e) => setH({ ...h, deal_type: e.value })} />
        </Field>
        {h.deal_type !== "CASH" ? (
          <>
            <Field label="Lender" required>
              <AppDropdown id="fi-lender" value={h.lender_id} options={opts["fi-lenders"] || []} onChange={(e) => setH({ ...h, lender_id: e.value })} />
            </Field>
            <Field label="Term (months)" required>
              <input type="number" min={1} className="form-control" style={inputStyle} value={h.term_months} onChange={(e) => setH({ ...h, term_months: e.target.value })} />
            </Field>
            <Field label="APR %">
              <input type="number" step="any" min={0} className="form-control" style={inputStyle} value={h.apr_pct} onChange={(e) => setH({ ...h, apr_pct: e.target.value })} />
            </Field>
            {h.deal_type === "LEASE" ? (
              <Field label="Residual value">
                <input type="number" step="any" min={0} className="form-control" style={inputStyle} value={h.residual_value} onChange={(e) => setH({ ...h, residual_value: e.target.value })} />
              </Field>
            ) : null}
          </>
        ) : null}
        <Field label="Down payment">
          <input type="number" step="any" min={0} className="form-control" style={inputStyle} value={h.down_payment} onChange={(e) => setH({ ...h, down_payment: e.target.value })} />
        </Field>
      </div>
      <h6>Products</h6>
      {products.map((p, i) => (
        <div key={i} className="d-flex gap-2 align-items-center mb-2">
          <div style={{ flex: 1 }}>
            <AppDropdown id={`fi-p-${i}`} value={p.product_id} options={(opts["fi-products"] || []).map((o) => ({ ...o, label: `${o.label} (${fmtMoney(o.row.price)})` }))} onChange={(e) => set(i, { product_id: e.value, price: "" })} />
          </div>
          <input type="number" step="any" min={0} className="form-control" style={{ ...inputStyle, width: 140 }} placeholder="List price" aria-label="Price" value={p.price} onChange={(e) => set(i, { price: e.target.value })} />
          <button type="button" style={linkBtn} onClick={() => setProducts(products.filter((_, k) => k !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setProducts([...products, { product_id: null, price: "" }])}>
        Add product
      </button>
      <div style={{ ...hint, marginTop: 8 }}>A product below its list price needs a sales manager's approval before the deal goes to the lender.</div>
    </Panel>
  );
}

function DealView({ id, onHide, onChanged }) {
  const { data: d, loading, error, reload } = useLoad(() => dmsService.getFiDeal(id), [id]);
  const [editing, setEditing] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const refresh = () => {
    reload();
    onChanged();
  };
  const go = (action, data) => run(() => dmsService.fiDealAction(id, action, data), refresh);
  const st = d ? d.status : null;
  return (
    <Panel title={d ? `F&I deal ${d.deal_number}` : "F&I deal"} onHide={onHide} width={900}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {d ? (
          <>
            <DocHead title="" number={`${d.customer_name} · ${d.so_number}`} status={st} sub={`${label(d.deal_type)}${d.lender_name ? ` with ${d.lender_name}` : ""}`}>
              {["DRAFT", "DECLINED"].includes(st) ? <SecondaryButton onClick={() => setEditing(true)}>Edit</SecondaryButton> : null}
              {["DRAFT", "DECLINED"].includes(st) && d.deal_type !== "CASH" ? <PrimaryButton onClick={() => go("submit")}>Send to lender</PrimaryButton> : null}
              {["SUBMITTED", "CONDITIONAL"].includes(st) ? <PrimaryButton onClick={() => setDeciding(true)}>Lender's answer</PrimaryButton> : null}
              {["APPROVED", "CONDITIONAL"].includes(st) || (d.deal_type === "CASH" && st === "DRAFT") ? <SecondaryButton onClick={() => go("contract", { action: "SEND" })}>Send contract</SecondaryButton> : null}
              {d.contract && d.contract.esign_status === "SENT" ? <SecondaryButton onClick={() => go("contract", { action: "SIGNED" })}>Mark signed</SecondaryButton> : null}
              {(st === "APPROVED" && d.contract && d.contract.esign_status === "SIGNED") || (d.deal_type === "CASH" && st === "DRAFT") ? <PrimaryButton onClick={() => window.confirm("Fund the deal? Invoices are created.") && go("fund")}>Fund</PrimaryButton> : null}
              {d.contract ? <SecondaryButton onClick={() => dmsService.fiContractPdf(id, `${d.deal_number}-contract.pdf`)}>Contract PDF</SecondaryButton> : null}
              {!["FUNDED", "CLOSED", "CANCELLED"].includes(st) ? <SecondaryButton onClick={() => window.confirm("Cancel the deal?") && go("cancel")}>Cancel</SecondaryButton> : null}
            </DocHead>
            <Section title="Structure">
              <DetailGrid
                items={[
                  ["Vehicle (order total)", `${d.currency} ${fmtMoney(d.vehicle_amount)}`],
                  ["Products", fmtMoney(d.products_total)],
                  ["Down payment", fmtMoney(d.down_payment)],
                  ["Amount financed", fmtMoney(d.amount_financed)],
                  ["Term / APR", d.deal_type === "CASH" ? "—" : `${d.term_months} months at ${Number(d.apr_pct)}%`],
                  ["Monthly payment", d.deal_type === "CASH" ? "—" : fmtMoney(d.monthly_payment)],
                  ["Residual", d.deal_type === "LEASE" ? fmtMoney(d.residual_value) : "—"],
                  ["Dealer reserve", fmtMoney(d.reserve_amount)],
                  ["Lender has paid", fmtMoney(d.amount_received)],
                  ["Contract", d.contract ? `${d.contract.contract_number} · ${label(d.contract.esign_status)}` : "—"],
                ]}
              />
            </Section>
            <Section title="Products">
              <DataTable
                rows={d.products}
                empty={<div style={hint}>No products on this deal.</div>}
                columns={[
                  { label: "Product", render: (p) => p.name },
                  { label: "Type", render: (p) => label(p.product_type) },
                  { label: "List", num: true, render: (p) => fmtMoney(p.list_price) },
                  { label: "Price", num: true, render: (p) => fmtMoney(p.price) },
                  { label: "VAT", num: true, render: (p) => fmtMoney(p.tax_amount) },
                  { label: "Margin", num: true, render: (p) => fmtMoney(Number(p.price) - Number(p.cost)) },
                ]}
              />
            </Section>
            {d.applications.length ? (
              <Section title="Lender applications">
                <DataTable
                  rows={d.applications}
                  columns={[
                    { label: "Lender", render: (a) => a.lender_name },
                    { label: "Sent", render: (a) => formatDate(a.submitted_at) },
                    { label: "Reference", render: (a) => a.lender_reference || "" },
                    { label: "Answer", render: (a) => <DmsBadge status={a.status} /> },
                    { label: "Approved", num: true, render: (a) => (a.approved_amount === null ? "" : fmtMoney(a.approved_amount)) },
                    { label: "Conditions", render: (a) => a.conditions || "" },
                  ]}
                />
              </Section>
            ) : null}
            <Section title="Action history">
              <ActionHistory actions={d.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      {editing && d ? <DealEditorPanel deal={d} onHide={() => setEditing(false)} onDone={refresh} /> : null}
      <FormDrawer
        show={deciding}
        title="The lender's answer"
        initial={{ decision: "APPROVED", approved_amount: d ? Number(d.amount_financed) : "" }}
        onHide={() => setDeciding(false)}
        onDone={refresh}
        onSave={(clean) => dmsService.fiDealAction(id, "lender-decision", { ...clean, approved_amount: clean.approved_amount === undefined ? undefined : Number(clean.approved_amount), approved_apr_pct: clean.approved_apr_pct === undefined ? undefined : Number(clean.approved_apr_pct) })}
        fields={[
          { key: "decision", label: "Answer", type: "select", required: true, options: ["APPROVED", "CONDITIONAL", "DECLINED"].map((v) => ({ label: label(v), value: v })) },
          { key: "approved_amount", label: "Amount approved", type: "number", step: "any", min: 0 },
          { key: "approved_apr_pct", label: "APR approved % (blank: as asked)", type: "number", step: "any", min: 0 },
          { key: "conditions", label: "Conditions", type: "textarea", max: 1000 },
          { key: "lender_reference", label: "Lender's reference", max: 60 },
        ]}
      />
    </Panel>
  );
}

export default function FiDeals() {
  const [status, setStatus] = useState("");
  const { data, loading, error, reload } = useLoad(() => dmsService.listFiDeals({ status: status || undefined }), [status]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="F&I Deals" subtitle="Finance, lease and products on vehicle sales" actions={<PrimaryButton onClick={() => setCreating(true)}>New deal</PrimaryButton>} />
      <div className="mb-3" style={{ width: 220 }}>
        <AppDropdown id="fi-st" value={status} options={[{ label: "All", value: "" }, ...["DRAFT", "PENDING_APPROVAL", "SUBMITTED", "APPROVED", "CONDITIONAL", "DECLINED", "FUNDED", "CLOSED", "CANCELLED"].map((s) => ({ label: label(s), value: s }))]} onChange={(e) => setStatus(e.value)} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No F&I deals" />}
          columns={[
            { label: "Deal", render: (r) => <b>{r.deal_number}</b> },
            { label: "Order", render: (r) => r.so_number },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Type", render: (r) => label(r.deal_type) },
            { label: "Lender", render: (r) => r.lender_name || "" },
            { label: "Financed", num: true, render: (r) => fmtMoney(r.amount_financed) },
            { label: "Monthly", num: true, render: (r) => fmtMoney(r.monthly_payment) },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <DealView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      {creating ? <DealEditorPanel onHide={() => setCreating(false)} onDone={(id) => {
            reload();
            setOpen(id);
          }} /> : null}
    </div>
  );
}
